/**
 * Run state machine + fixed-tick simulation loop.
 *
 * Phases: idle → running → (gate_decision) → pedestal → mecha → climax → results
 * Fail: count → 0 or mecha HP → 0 → continue_offer / results_lose
 */
import { FORWARD_SPEED, LATERAL_SPEED, PATH_HALF_WIDTH, SIM_API_VERSION, TICK_DT, TICK_HZ, } from "./constants.js";
import { createWaveState, friendlyDps, isInWaveContact, resolveCombatTick, } from "./combat.js";
import { createCountState, setCount } from "./count.js";
import { formationSlots } from "./formation.js";
import { applyGateOp } from "./gates.js";
import { gateGroupsAtZ, loadLevel, pickGateByLateral } from "./level.js";
import { applyTransform, canTransform } from "./transform.js";
function transition(run, to) {
    if (run.phase === to)
        return;
    const from = run.phase;
    run.phase = to;
    run.pendingEvents.push({ type: "phase", from, to });
}
function emit(run, e) {
    run.pendingEvents.push(e);
}
function clampLateral(x) {
    return Math.max(-PATH_HALF_WIDTH, Math.min(PATH_HALF_WIDTH, x));
}
function rebuildWaves(level) {
    return (level.waves ?? []).map(createWaveState);
}
/**
 * Create a new run in `idle`. Call `startRun` or pass autoStart.
 */
export function createRun(options) {
    const level = loadLevel(options.level);
    const run = {
        config: {
            level,
            maxContinues: options.maxContinues ?? 1,
            seed: options.seed ?? 0,
        },
        phase: "idle",
        tick: 0,
        time: 0,
        count: createCountState(level.startN),
        pathZ: 0,
        lateralX: 0,
        input: { lateral: 0 },
        mecha: null,
        inMecha: false,
        waves: rebuildWaves(level),
        climax: level.climaxWave ? createWaveState(level.climaxWave) : null,
        activeWave: null,
        triggeredGateZs: new Set(),
        pedestalDone: false,
        continuesUsed: 0,
        pendingEvents: [],
    };
    return run;
}
export function startRun(run) {
    if (run.phase !== "idle" && run.phase !== "results_win" && run.phase !== "results_lose") {
        return;
    }
    // Reset for fresh start from idle/results.
    const level = run.config.level;
    run.tick = 0;
    run.time = 0;
    run.count = createCountState(level.startN);
    run.pathZ = 0;
    run.lateralX = 0;
    run.mecha = null;
    run.inMecha = false;
    run.waves = rebuildWaves(level);
    run.climax = level.climaxWave ? createWaveState(level.climaxWave) : null;
    run.activeWave = null;
    run.triggeredGateZs = new Set();
    run.pedestalDone = false;
    run.continuesUsed = 0;
    run.pendingEvents = [];
    transition(run, "running");
}
export function setInput(run, input) {
    run.input = {
        lateral: Math.max(-1, Math.min(1, input.lateral)),
    };
}
function failRun(run, reason) {
    emit(run, { type: "fail", reason });
    if (run.continuesUsed < run.config.maxContinues) {
        transition(run, "continue_offer");
    }
    else {
        transition(run, "results_lose");
    }
}
/** Accept continue: restore minimal N or mecha HP at checkpoint (current pathZ). */
export function acceptContinue(run) {
    if (run.phase !== "continue_offer")
        return false;
    run.continuesUsed += 1;
    if (run.inMecha && run.mecha) {
        const restored = Math.max(1, Math.floor(run.mecha.maxHp * 0.35));
        run.mecha = { ...run.mecha, hp: restored };
        transition(run, run.climax && !run.climax.cleared ? "climax" : "mecha");
    }
    else {
        run.count = setCount(run.count, Math.max(3, run.config.level.startN));
        transition(run, "running");
    }
    return true;
}
export function declineContinue(run) {
    if (run.phase !== "continue_offer")
        return;
    transition(run, "results_lose");
}
function tryGates(run) {
    const groups = gateGroupsAtZ(run.config.level);
    for (const [z, gates] of groups) {
        if (run.triggeredGateZs.has(z))
            continue;
        // Cross gate when pathZ reaches gate Z.
        if (run.pathZ < z)
            continue;
        run.triggeredGateZs.add(z);
        if (gates.length > 1) {
            transition(run, "gate_decision");
        }
        const chosen = pickGateByLateral(gates, run.lateralX);
        const nBefore = run.count.n;
        const nAfter = applyGateOp(nBefore, chosen.op, chosen.k);
        run.count = setCount(run.count, nAfter);
        emit(run, {
            type: "gate_applied",
            gateId: chosen.id,
            op: chosen.op,
            k: chosen.k,
            nBefore,
            nAfter: run.count.n,
        });
        emit(run, {
            type: "n_changed",
            nBefore,
            nAfter: run.count.n,
            reason: `gate:${chosen.id}`,
        });
        if (run.phase === "gate_decision") {
            transition(run, "running");
        }
        if (run.count.n <= 0 && !run.inMecha) {
            failRun(run, "count_zero");
            return;
        }
    }
}
function tryPedestal(run) {
    const ped = run.config.level.pedestal;
    if (!ped || run.pedestalDone || run.inMecha)
        return;
    if (run.pathZ < ped.z)
        return;
    if (!canTransform(run.count.n, ped)) {
        // Failed minN — skip transform, keep running (or treat as soft fail).
        run.pedestalDone = true;
        return;
    }
    transition(run, "pedestal");
    const nPre = run.count.n;
    const { mecha, nAfter } = applyTransform(nPre);
    run.count = setCount(run.count, nAfter);
    run.mecha = mecha;
    run.inMecha = true;
    run.pedestalDone = true;
    emit(run, { type: "transform", nPre, mecha });
    emit(run, {
        type: "n_changed",
        nBefore: nPre,
        nAfter,
        reason: "transform",
    });
    if (run.climax) {
        transition(run, "climax");
    }
    else {
        transition(run, "mecha");
    }
}
function updateCombat(run) {
    // Pre-transform waves
    if (!run.inMecha) {
        for (const wave of run.waves) {
            if (wave.cleared)
                continue;
            const contact = isInWaveContact(run.pathZ, wave.def);
            wave.inContact = contact;
            if (contact) {
                if (run.activeWave !== wave) {
                    run.activeWave = wave;
                    emit(run, { type: "wave_contact", waveId: wave.def.id });
                }
                const result = resolveCombatTick(wave, run.count.n, null, TICK_DT);
                Object.assign(wave, result.wave);
                if (result.nAfter !== run.count.n) {
                    const nBefore = run.count.n;
                    run.count = setCount(run.count, result.nAfter);
                    emit(run, {
                        type: "n_changed",
                        nBefore,
                        nAfter: run.count.n,
                        reason: "combat",
                    });
                }
                if (wave.cleared) {
                    emit(run, { type: "wave_cleared", waveId: wave.def.id });
                    if (run.activeWave === wave)
                        run.activeWave = null;
                }
                if (run.count.n <= 0) {
                    failRun(run, "count_zero");
                    return;
                }
            }
        }
        return;
    }
    // Mecha / climax combat
    if (run.mecha && run.climax && !run.climax.cleared) {
        const wave = run.climax;
        const contact = isInWaveContact(run.pathZ, wave.def) || run.phase === "climax";
        // Once in climax phase, force contact for the horde.
        wave.inContact = contact || run.phase === "climax";
        if (wave.inContact) {
            run.activeWave = wave;
            const result = resolveCombatTick(wave, 0, run.mecha, TICK_DT);
            Object.assign(wave, result.wave);
            if (result.mechaHpAfter !== null && run.mecha) {
                run.mecha = { ...run.mecha, hp: result.mechaHpAfter };
            }
            if (wave.cleared) {
                emit(run, { type: "wave_cleared", waveId: wave.def.id });
                run.activeWave = null;
                emit(run, { type: "win" });
                transition(run, "results_win");
                return;
            }
            if (run.mecha && run.mecha.hp <= 0) {
                failRun(run, "mecha_dead");
            }
        }
    }
    else if (run.inMecha && run.mecha && (!run.climax || run.climax.cleared)) {
        // No climax or already cleared — win when path ends.
    }
}
function checkPathEnd(run) {
    if (run.pathZ < run.config.level.pathLength)
        return;
    if (run.phase === "results_win" || run.phase === "results_lose")
        return;
    if (run.inMecha && run.climax && !run.climax.cleared) {
        // Stay in climax until horde dies or mecha dies (combat handles it).
        if (run.phase !== "climax")
            transition(run, "climax");
        return;
    }
    if (run.count.n <= 0 && !run.inMecha) {
        failRun(run, "count_zero");
        return;
    }
    emit(run, { type: "win" });
    transition(run, "results_win");
}
/**
 * Advance simulation by one fixed tick (default 20 Hz).
 * Terminal phases are no-ops.
 */
export function tick(run, dt = TICK_DT) {
    run.pendingEvents = [];
    const terminal = [
        "idle",
        "results_win",
        "results_lose",
        "continue_offer",
    ];
    if (terminal.includes(run.phase)) {
        return snapshot(run);
    }
    // Pedestal cinematic is one-tick juice phase then continues.
    // Steer + forward
    run.lateralX = clampLateral(run.lateralX + run.input.lateral * LATERAL_SPEED * dt);
    run.pathZ = Math.min(run.config.level.pathLength, run.pathZ + FORWARD_SPEED * dt);
    run.tick += 1;
    run.time += dt;
    if (!run.inMecha) {
        tryGates(run);
        if (run.phase === "continue_offer" || run.phase === "results_lose") {
            return snapshot(run);
        }
        tryPedestal(run);
    }
    updateCombat(run);
    if (run.phase === "continue_offer" || run.phase === "results_lose" || run.phase === "results_win") {
        return snapshot(run);
    }
    checkPathEnd(run);
    return snapshot(run);
}
/** Advance multiple ticks (e.g. catch-up). */
export function tickN(run, n, dt = TICK_DT) {
    let snap = snapshot(run);
    for (let i = 0; i < n; i++) {
        snap = tick(run, dt);
    }
    return snap;
}
export function snapshot(run) {
    const mecha = run.mecha;
    const active = run.activeWave;
    const events = run.pendingEvents.slice();
    return {
        apiVersion: SIM_API_VERSION,
        tick: run.tick,
        time: run.time,
        phase: run.phase,
        n: run.count.n,
        powerTokens: run.count.powerTokens,
        pathZ: run.pathZ,
        lateralX: run.lateralX,
        pathLength: run.config.level.pathLength,
        inMecha: run.inMecha,
        mecha: mecha ? { ...mecha } : null,
        friendlyDps: friendlyDps(run.count.n, mecha),
        activeWaveId: active && !active.cleared ? active.def.id : null,
        activeWaveHp: active ? active.hp : 0,
        activeWaveMaxHp: active ? active.maxHp : 0,
        formation: formationSlots(run.inMecha ? 0 : run.count.n),
        levelId: run.config.level.id,
        continuesUsed: run.continuesUsed,
        events,
    };
}
/** Read-only accessors for tests / tools. */
export function getPhase(run) {
    return run.phase;
}
export function getCount(run) {
    return run.count.n;
}
export function getMecha(run) {
    return run.mecha ? { ...run.mecha } : null;
}
export { TICK_DT, TICK_HZ };
//# sourceMappingURL=run.js.map